import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { container } from '@/app/container';
import { CatalogRepositoryToken } from '../../di/tokens';
import type {
  CreateProductInput,
  ProductStatus,
  UpdateProductExternalKeyInput,
} from '../../domain/entities/catalog.entity';

export function useProducts(page = 1, perPage = 10) {
  const repository = container.resolve(CatalogRepositoryToken);
  return useQuery({
    queryKey: ['catalog', 'products', page, perPage],
    queryFn: () => repository.listProducts({ page, perPage }),
    staleTime: 1000 * 60 * 5,
    placeholderData: keepPreviousData,
  });
}

export function useCreateProduct() {
  const queryClient = useQueryClient();
  const repository = container.resolve(CatalogRepositoryToken);
  return useMutation({
    mutationFn: (input: CreateProductInput) => repository.createProduct(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['catalog', 'products'],
      });
    },
  });
}

export function useUpdateProductStatus(productId: string) {
  const queryClient = useQueryClient();
  const repository = container.resolve(CatalogRepositoryToken);
  return useMutation({
    mutationFn: (status: ProductStatus) =>
      repository.updateProductStatus(productId, { status }),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['catalog', 'products'],
      });
    },
  });
}

export function useUpdateProductExternalKey(productId: string) {
  const queryClient = useQueryClient();
  const repository = container.resolve(CatalogRepositoryToken);
  return useMutation({
    mutationFn: (input: UpdateProductExternalKeyInput) =>
      repository.updateProductExternalKey(productId, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['catalog', 'products'],
      });
    },
  });
}
